import { AppSidebar, SidebarTriggerButton } from "@/components/layout/AppSidebar";
import { TrialBanner } from "@/components/trial/TrialBanner";
import { UpgradeModal } from "@/components/trial/UpgradeModal";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { resetPostHog } from "@/lib/analytics/posthog";
import { authClient } from "@/lib/auth/client";
import { FleetModeProvider } from "@/lib/tms/fleet-mode-context";
import { cn } from "@/lib/utils";
import * as React from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

const SIDEBAR_KEY = "shippeek-sidebar-open";

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );

  React.useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

export function AppLayout({ connected }: { connected: boolean }) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    if (typeof window === "undefined") return true;
    const stored = localStorage.getItem(SIDEBAR_KEY);
    return stored !== null ? stored === "true" : true;
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [orgName, setOrgName] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetch("/api/account/team", { credentials: "include" })
      .then((r) => r.json())
      .then((json: { data?: { organization?: { name?: string } } }) => {
        if (!cancelled && json.data?.organization?.name) {
          setOrgName(json.data.organization.name);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      setMobileSidebarOpen((current) => !current);
      return;
    }

    setSidebarOpen((current) => {
      const next = !current;
      localStorage.setItem(SIDEBAR_KEY, String(next));
      return next;
    });
  }, [isMobile]);

  React.useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname, location.search]);

  const handleDisconnect = React.useCallback(async () => {
    await authClient.signOut();
    void resetPostHog();
    navigate("/auth/login", { replace: true });
  }, [navigate]);

  if (!connected) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <SubscriptionProvider>
    <FleetModeProvider>
      <div className="flex h-screen overflow-hidden bg-background text-foreground">
        {/* Desktop sidebar */}
        {!isMobile ? (
          <aside
            className={cn(
              "hidden shrink-0 border-r border-border/60 bg-card transition-all duration-200 md:block",
              sidebarOpen ? "w-60" : "w-14",
            )}
          >
            <AppSidebar collapsed={!sidebarOpen} onDisconnect={handleDisconnect} />
          </aside>
        ) : null}

        {/* Mobile sidebar overlay */}
        {isMobile && mobileSidebarOpen ? (
          <div className="fixed inset-0 z-50 md:hidden">
            <button
              className="absolute inset-0 bg-black/20 backdrop-blur-sm"
              aria-label="Close navigation"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <aside className="absolute inset-y-0 left-0 w-72 border-r border-border/60 bg-card shadow-lg">
              <AppSidebar onNavigate={() => setMobileSidebarOpen(false)} onDisconnect={handleDisconnect} />
            </aside>
          </div>
        ) : null}

        {/* Main content */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border/60 bg-card px-5">
            <SidebarTriggerButton onClick={toggleSidebar} />
            {orgName ? (
              <>
                <div className="h-4 w-px bg-border" />
                <span className="truncate text-xs font-medium uppercase tracking-wider text-muted-foreground font-mono">{orgName}</span>
              </>
            ) : null}
          </header>

          <TrialBanner />

          <main className="flex-1 overflow-auto p-6">
            <Outlet />
          </main>
        </div>

        <UpgradeModal />
      </div>
    </FleetModeProvider>
    </SubscriptionProvider>
  );
}
